import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getAccessToken, ApiClientError } from '../../lib/api/client.js';
import { TableSkeleton, Empty, ErrorBanner, Badge } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';

/**
 * Uploaded audio.
 *
 * One screen for everything that plays to a caller, filtered by what it is for.
 * Splitting prompts, greetings and hold music into separate pages would mean
 * three uploaders and three empty states for one job.
 *
 * Audio is converted to 8 kHz mono on the way in, so what is listed here is
 * what the switch will actually play — not what was uploaded.
 */

interface MediaFile {
  id: string; kind: string; name: string; description: string | null;
  bytes: number | null; duration_sec: number | null;
  sample_rate: number | null; channels: number | null;
  original_name: string | null; created_at: string;
}

const KINDS = [
  { key: 'greeting', label: 'Greetings', icon: 'ivr',
    hint: 'Played when an IVR answers' },
  { key: 'prompt', label: 'Prompts', icon: 'media',
    hint: 'Menu options and instructions' },
  { key: 'moh', label: 'Music on hold', icon: 'clock',
    hint: 'Played while a caller waits' },
  { key: 'announcement', label: 'Announcements', icon: 'phone',
    hint: 'Notices before a call connects' },
] as const;

const fmtBytes = (n: number | null): string =>
  n === null ? '—' : n < 1024 ? `${n} B` : `${(n / 1024).toFixed(0)} KB`;

const fmtDuration = (s: number | null): string => {
  if (s === null) return '—';
  if (s < 60) return `${s.toFixed(1)}s`;
  return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
};

/** The time on a player: 0:04, 1:12. */
const clock = (s: number): string => {
  const t = Math.max(0, Math.floor(s));
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`;
};

export function MediaTab({ tenantId, section = 'prompts' }: {
  tenantId: string;
  /** Prompts and greetings, or music on hold: two screens over one library (docs/26 E2). */
  section?: 'prompts' | 'moh';
}) {
  const qc = useQueryClient();
  const kinds = KINDS.filter((k) => (section === 'moh') === (k.key === 'moh'));
  const [kind, setKind] = useState<string>(section === 'moh' ? 'moh' : 'greeting');
  /** The file loaded into the player, with an object URL of its audio. */
  const [current, setCurrent] = useState<{ id: string; url: string } | null>(null);
  const [playback, setPlayback] = useState({ playing: false, time: 0, duration: 0 });
  const [loading, setLoading] = useState<string | null>(null);
  const [playError, setPlayError] = useState<{ id: string; message: string } | null>(null);
  const audio = useRef<HTMLAudioElement | null>(null);
  // An object URL holds the audio in memory until it is revoked: the old one
  // goes when another file loads, and the last one when the page closes.
  useEffect(() => () => { if (current) URL.revokeObjectURL(current.url); }, [current]);

  const q = useQuery({
    queryKey: ['media', tenantId],
    queryFn: () => api.get<{
      data: MediaFile[]; conversion_available: boolean;
      /** What this node's sox can actually read. Absent on an older API. */
      formats?: string[]; max_bytes?: number;
    }>(`/v1/tenants/${tenantId}/media`),
    enabled: !!tenantId,
  });

  const del = useMutation({
    mutationFn: (id: string) => api.delete<{ references_cleared: number }>(
      `/v1/tenants/${tenantId}/media/${id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['media', tenantId] }); },
  });

  const files = (q.data?.data ?? []).filter((f) => f.kind === kind);

  /**
   * Plays a file in its own row: its button turns to pause, and a seek bar and
   * the time appear on the row, which keeps its height. Playing another file
   * stops this one. It was a player that opened under the row, which pushed the
   * list about while checking one greeting after another (14 September 2026).
   *
   * Loaded as a blob rather than pointing <audio> at the URL: the endpoint needs
   * an Authorization header, which an <audio src> cannot send. Playing a blob
   * needs `media-src blob:` in the CSP (roles/nginx, security-headers), which it
   * never had -- and failures returned without a word -- so the button did
   * nothing and said nothing (14 September 2026).
   */
  const play = async (f: MediaFile): Promise<void> => {
    const el = audio.current;
    if (!el) return;
    setPlayError(null);
    if (current?.id === f.id) {
      if (el.paused) {
        if (el.ended) el.currentTime = 0;
        void el.play();
      } else {
        el.pause();
      }
      return;
    }
    el.pause();
    setLoading(f.id);
    try {
      const res = await fetch(`/api/v1/tenants/${tenantId}/media/${f.id}/audio`, {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) {
        setPlayError({
          id: f.id,
          message: res.status === 404
            ? 'The record exists but its audio is missing on the node.'
            : res.status === 401
              ? 'Your session has expired. Reload the page and try again.'
              : `The audio could not be loaded (${res.status}).`,
        });
        return;
      }
      const url = URL.createObjectURL(await res.blob());
      setPlayback({ playing: false, time: 0, duration: f.duration_sec ?? 0 });
      setCurrent({ id: f.id, url });
      el.src = url;
      await el.play();
    } catch (e) {
      // A browser that wants a fresh click after the download: the file is
      // loaded, so the next press plays it.
      setPlayError({
        id: f.id,
        message: e instanceof DOMException && e.name === 'NotAllowedError'
          ? 'Press play again to start it.'
          : 'The audio could not be played.',
      });
    } finally {
      setLoading(null);
    }
  };

  /** Jumps to a fraction of the way through the file that is loaded. */
  const seek = (fraction: number) => {
    const el = audio.current;
    const d = playback.duration || el?.duration || 0;
    if (!el || !Number.isFinite(d) || d <= 0) return;
    el.currentTime = Math.max(0, Math.min(d, fraction * d));
  };

  return (
    <>
      <div style={{ marginBottom: 14 }}>
        <h2 className="arc-h2">{section === 'moh' ? 'Music on hold' : 'Prompts & greetings'}</h2>
        <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 2 }}>
          {section === 'moh'
            ? 'What a caller hears while they wait.'
            : 'What a caller hears from menus, greetings and announcements.'}
          {' '}Uploads are converted to 8 kHz mono, which is what the switch plays.
        </p>
        {/* Said where the files are, rather than found out on a call: nothing
            chooses one of these yet, so held calls and queues play the
            platform's own music (vars.xml hold_music). */}
        {section === 'moh' && (
          <p className="arc-subtle" style={{ fontSize: 12, marginTop: 4 }}>
            Held calls and queues still play the platform's own music; choosing one of these
            for the account is not built yet.
          </p>
        )}
      </div>

      {q.error && <ErrorBanner error={q.error} />}
      {del.error && <ErrorBanner error={del.error} />}

      {q.data && !q.data.conversion_available && (
        <div className="arc-banner arc-banner-danger" style={{ marginBottom: 14 }}>
          <Icon name="warning" size={14} />
          <span>
            Audio conversion is unavailable on this node, so uploads will be
            refused rather than stored in a format the switch may not play.
          </span>
        </div>
      )}

      {/* Cards rather than a row of pills. Four kinds with different purposes
          is a choice, not a filter, and the hint that explains each one has
          nowhere to live on a button. */}
      {kinds.length > 1 && (
      <div className="arc-kind-grid">
        {kinds.map((k) => {
          const n = (q.data?.data ?? []).filter((f) => f.kind === k.key).length;
          return (
            <button key={k.key} type="button" className="arc-kind"
              data-on={kind === k.key} aria-pressed={kind === k.key}
              onClick={() => setKind(k.key)}>
              <span className="arc-kind-icon"><Icon name={k.icon} size={18} /></span>
              <span className="arc-kind-body">
                <span className="arc-kind-label">{k.label}</span>
                <span className="arc-kind-hint">{k.hint}</span>
              </span>
              <span className="arc-kind-count">{n}</span>
            </button>
          );
        })}
      </div>
      )}

      <UploadCard tenantId={tenantId} kind={kind}
        formats={q.data?.formats}
        maxBytes={q.data?.max_bytes}
        onDone={() => void qc.invalidateQueries({ queryKey: ['media', tenantId] })} />

      <div className="arc-card">
        <div className="arc-card-head">
          <h2 className="arc-h2">{KINDS.find((k) => k.key === kind)?.label}</h2>
          <span className="arc-subtle">{KINDS.find((k) => k.key === kind)?.hint}</span>
        </div>
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th style={{ width: 44 }} />
                <th>Name</th><th>Uploaded from</th>
                <th className="arc-td-num">Length</th>
                <th className="arc-td-num">Size</th>
                <th />
              </tr>
            </thead>
            {q.isLoading ? <TableSkeleton cols={6} /> : (
              <tbody>
                {files.map((f) => {
                  const isCurrent = current?.id === f.id;
                  const playing = isCurrent && playback.playing;
                  const length = isCurrent ? playback.duration || f.duration_sec || 0 : 0;
                  return (
                    <tr key={f.id}>
                      <td>
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon"
                          aria-label={playing ? `Pause ${f.name}` : `Play ${f.name}`}
                          title={playing ? 'Pause' : 'Play'}
                          disabled={loading === f.id}
                          onClick={() => void play(f)}>
                          <Icon name={playing ? 'pause' : 'play'} size={15} />
                        </button>
                      </td>
                      <td className={isCurrent ? 'arc-audio-cell' : undefined}>
                        <div className="arc-stack">
                          <span style={{ fontWeight: 550 }}>{f.name}</span>
                          {f.description && (
                            <span className="arc-subtle" style={{ fontSize: 11 }}>{f.description}</span>
                          )}
                          {playError?.id === f.id && (
                            <span className="arc-error" style={{ margin: 0 }} role="alert">{playError.message}</span>
                          )}
                        </div>
                        {isCurrent && length > 0 && (
                          <div className="arc-audio-seek" role="slider" tabIndex={0}
                            aria-label={`Position in ${f.name}`}
                            aria-valuemin={0} aria-valuemax={Math.round(length)}
                            aria-valuenow={Math.round(playback.time)}
                            aria-valuetext={`${clock(playback.time)} of ${clock(length)}`}
                            onClick={(e) => {
                              const r = e.currentTarget.getBoundingClientRect();
                              seek((e.clientX - r.left) / r.width);
                            }}
                            onKeyDown={(e) => {
                              const to = e.key === 'ArrowRight' ? (playback.time + 5) / length
                                : e.key === 'ArrowLeft' ? (playback.time - 5) / length
                                  : e.key === 'Home' ? 0 : e.key === 'End' ? 1 : null;
                              if (to === null) return;
                              e.preventDefault();
                              seek(to);
                            }}>
                            <div className="arc-audio-seek-fill"
                              style={{ width: `${Math.min(100, (playback.time / length) * 100)}%` }} />
                          </div>
                        )}
                      </td>
                      <td className="arc-muted arc-truncate" style={{ maxWidth: 200 }}>
                        {f.original_name ?? <span className="arc-subtle">—</span>}
                      </td>
                      <td className="arc-td-num arc-muted">
                        {isCurrent && length > 0
                          ? <span className="arc-num">{clock(playback.time)} / {clock(length)}</span>
                          : fmtDuration(f.duration_sec)}
                      </td>
                      <td className="arc-td-num arc-muted">{fmtBytes(f.bytes)}</td>
                      <td className="arc-td-right">
                        <div className="arc-row arc-row-actions" style={{ justifyContent: 'flex-end' }}>
                          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                            onClick={() => {
                              if (confirm(`Delete "${f.name}"? Anything using it falls back to silence.`)) {
                                if (isCurrent) { audio.current?.pause(); setCurrent(null); }
                                del.mutate(f.id);
                              }
                            }}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            )}
          </table>
        </div>
        {!q.isLoading && files.length === 0 && (
          <Empty icon="media" title={`No ${KINDS.find((k) => k.key === kind)?.label.toLowerCase()}`}
            hint="Upload a WAV, MP3, FLAC or OGG file above." />
        )}
      </div>

      {/* One element for the page: playing another file stops this one, so a
          page of prompts never talks over itself. */}
      <audio ref={audio} hidden preload="auto"
        onPlay={() => setPlayback((p) => ({ ...p, playing: true }))}
        onPause={() => setPlayback((p) => ({ ...p, playing: false }))}
        onEnded={() => setPlayback((p) => ({ ...p, playing: false, time: p.duration }))}
        onTimeUpdate={(e) => {
          const time = e.currentTarget.currentTime;
          setPlayback((p) => ({ ...p, time }));
        }}
        onLoadedMetadata={(e) => {
          const duration = e.currentTarget.duration;
          if (Number.isFinite(duration) && duration > 0) setPlayback((p) => ({ ...p, duration }));
        }}
        onError={() => {
          if (current) setPlayError({ id: current.id, message: 'This browser could not play the file.' });
        }} />
    </>
  );
}

function UploadCard({ tenantId, kind, onDone, formats, maxBytes }: {
  tenantId: string; kind: string; onDone: () => void;
  /** What this node can read, from the API. Undefined until the list loads. */
  formats?: string[] | undefined;
  maxBytes?: number | undefined;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  /**
   * Undefined means the list has not loaded yet, not that nothing is supported --
   * so the input stays permissive until the node has answered, rather than
   * briefly refusing everything.
   */
  const accept = formats && formats.length > 0
    ? `audio/*,${formats.map((f) => `.${f}`).join(',')}`
    : 'audio/*';
  const mb = Math.round((maxBytes ?? 20 * 1024 * 1024) / 1024 / 1024);
  const hint = formats === undefined
    ? `Up to ${mb} MB.`
    : formats.length === 0
      ? 'This node has no audio conversion tooling, so no upload will be accepted.'
      : `${formats.map((f) => f.toUpperCase()).join(', ')}. Up to ${mb} MB.`;

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const body = new FormData();
      body.append('kind', kind);
      body.append('name', name);
      if (description) body.append('description', description);
      body.append('file', file);

      // FormData, so no Content-Type header: the browser has to set it with
      // the multipart boundary, and naming it ourselves omits the boundary and
      // makes the server unable to parse a body it just received.
      const res = await fetch(`/api/v1/tenants/${tenantId}/media`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body,
      });
      if (!res.ok) {
        const problem = await res.json().catch(() => null);
        throw new Error(problem?.detail ?? problem?.title ?? `Upload failed (${res.status})`);
      }
      setName(''); setDescription(''); setFile(null);
      if (inputRef.current) inputRef.current.value = '';
      onDone();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message
        : err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="arc-card arc-card-body" style={{ marginBottom: 14 }} onSubmit={submit}>
      {error && (
        <div className="arc-banner arc-banner-danger" style={{ marginBottom: 12 }} role="alert">
          <Icon name="warning" size={14} /><span>{error}</span>
        </div>
      )}
      <div style={{
        display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.2fr) minmax(0,1.4fr) auto',
        gap: 12, alignItems: 'start',
      }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="md-name">Name</label>
          <input id="md-name" className="arc-input" required value={name}
            onChange={(e) => setName(e.target.value)} placeholder="Main greeting" />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="md-desc">Description</label>
          <input id="md-desc" className="arc-input" value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional — what it says" />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="md-file">Audio file</label>
          {/*
            Built from what the node answered, not from a list written here.
            The hard-coded version offered MP3 on a node whose sox could not
            decode one, and the refusal read as though the file were at fault --
            which is the bug this screen was reported for.
          */}
          <input id="md-file" ref={inputRef} className="arc-input" type="file"
            accept={accept} required
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              setFile(f);
              // Saves typing the obvious thing: the filename is nearly always
              // what the prompt should be called.
              if (f && !name) setName(f.name.replace(/\.[^.]+$/, ''));
            }} />
          <p className="arc-hint">{hint}</p>
        </div>
        <div style={{ paddingTop: 25 }}>
          <button type="submit" className="arc-btn arc-btn-primary" disabled={busy || !file}>
            {busy ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </div>
    </form>
  );
}
